import { useCallback, useEffect, useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useQueryState } from "nuqs";
import type { RouterOutputs } from "@bibacv/api-contracts";
import { orpc } from "@/lib/orpc";
import { PageHeader } from "@/components/layout/page-header";
import { UnsavedContext } from "@/routes/stores/-components/unsaved";
import { StoreSectionBar, StoreSidebar } from "@/routes/stores/-components/store-sidebar";
import { resolveSection } from "@/routes/stores/-components/store-sections";
import { GeneralSettings } from "@/routes/stores/-components/general-settings";
import { CamerasSettings } from "@/routes/stores/-components/cameras-settings";
import { ZonesSettings } from "@/routes/stores/-components/zones-settings";
import { IngestSettings } from "@/routes/stores/-components/ingest-settings";
import { PerceptionSettings } from "@/routes/stores/-components/perception-settings";
import { AnalyticsSettings } from "@/routes/stores/-components/analytics-settings";
import { VlmSettings } from "@/routes/stores/-components/vlm-settings";
import { RunsSettings } from "@/routes/stores/-components/runs-settings";
import { PipelineProfiler } from "@/routes/stores/-components/pipeline-profiler";
import { ScheduleSettings } from "@/routes/stores/-components/schedule-settings";
import { ServiceSettings } from "@/routes/stores/-components/service-settings";

export const Route = createFileRoute("/stores/$storeSlug")({
  ssr: false,
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      orpc.store.get.queryOptions({ input: { slug: params.storeSlug } }),
    ),
  head: ({ loaderData }) => ({
    meta: [{ title: loaderData ? `${loaderData.name} · BibaCV` : "BibaCV" }],
  }),
  component: StoreSettingsPage,
});

function StoreSettingsPage() {
  const { storeSlug } = Route.useParams();
  const { data: store } = useSuspenseQuery(
    orpc.store.get.queryOptions({ input: { slug: storeSlug } }),
  );
  const [tab, setTab] = useQueryState("tab", { defaultValue: "general" });

  const dirtyRef = useRef(false);
  const registerDirty = useCallback((dirty: boolean) => {
    dirtyRef.current = dirty;
  }, []);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (dirtyRef.current) event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);

  const section = resolveSection(tab);
  const select = (value: string) => {
    if (
      dirtyRef.current &&
      !window.confirm("This section has unsaved changes that will be lost. Leave anyway?")
    ) {
      return;
    }
    void setTab(value);
  };

  return (
    <UnsavedContext.Provider value={registerDirty}>
      <PageHeader title={store.name} eyebrow={store.slug} />

      <div className="flex flex-1 items-start">
        <StoreSidebar store={store} section={section} onSelect={select} />

        <div className="min-w-0 flex-1">
          <StoreSectionBar section={section} onSelect={select} />
          <div className="w-full px-6 py-7 md:px-8">
            <StoreSection key={store.slug} section={section} store={store} />
          </div>
        </div>
      </div>
    </UnsavedContext.Provider>
  );
}

type StoreDetail = RouterOutputs["store"]["get"];

function StoreSection({ section, store }: { section: string; store: StoreDetail }) {
  switch (section) {
    case "cameras":
      return <CamerasSettings store={store} />;
    case "zones":
      return <ZonesSettings store={store} />;
    case "ingest":
      return <IngestSettings store={store} />;
    case "perception":
      return <PerceptionSettings store={store} />;
    case "analytics":
      return <AnalyticsSettings store={store} />;
    case "vlm":
      return <VlmSettings store={store} />;
    case "runs":
      return <RunsSettings store={store} />;
    case "profiler":
      return <PipelineProfiler store={store} />;
    case "schedule":
      return <ScheduleSettings store={store} />;
    case "service":
      return <ServiceSettings store={store} />;
    default:
      return <GeneralSettings store={store} />;
  }
}
